import {CardHelp} from '../shared/components/CardHelp';
import { useState, useEffect } from 'react';
import { Save, RotateCcw, Loader2, CheckCircle, AlertCircle, Plus, X } from 'lucide-react';
import { useSettings } from '../shared/hooks/useSettings';
import api from 'axios';
import {OperationalScoreWeightKey} from '../shared/types/inventory';
import {
  DEFAULT_OPERATIONAL_SCORE_WEIGHTS,
  OPERATIONAL_SCORE_WEIGHT_OPTIONS,
  operationalScoreWeightsEqual,
} from '../shared/config/operationalScore';

const normalizeCnpj = (value: string) => value.replace(/\D/g, '');

const formatCnpj = (value: string) => {
  const digits = normalizeCnpj(value);
  if (digits.length !== 14) return digits;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const normalizeCnpjList = (values: string[]) => Array.from(new Set(
  values.map(normalizeCnpj).filter((cnpj) => cnpj.length === 9 || cnpj.length === 14),
)).sort();

const areCnpjListsEqual = (left: string[], right: string[]) => (
  normalizeCnpjList(left).join('|') === normalizeCnpjList(right).join('|')
);

export const SettingsPage = () => {
  const { settings, isLoading, error: settingsError, reload } = useSettings();
  const [leadTime, setLeadTime] = useState(settings.leadTime);
  const [diasCoberturaAlvo, setDiasCoberturaAlvo] = useState(settings.diasCoberturaAlvo);
  const [calculationPeriodDays, setCalculationPeriodDays] = useState<number | null>(settings.calculationPeriodDays);
  const [excludeNoSalesNoStock, setExcludeNoSalesNoStock] = useState(settings.excludeNoSalesNoStock);
  const [excludeNoSalesWithStock, setExcludeNoSalesWithStock] = useState(settings.excludeNoSalesWithStock);
  const [importCnpjFilterEnabled, setImportCnpjFilterEnabled] = useState(settings.importCnpjFilterEnabled);
  const [importAllowedCnpjs, setImportAllowedCnpjs] = useState<string[]>(settings.importAllowedCnpjs);
  const [operationalScoreWeights, setOperationalScoreWeights] = useState({...settings.operationalScoreWeights});
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjInputError, setCnpjInputError] = useState('');
  const [savedLeadTime, setSavedLeadTime] = useState(settings.leadTime);
  const [savedDiasCoberturaAlvo, setSavedDiasCoberturaAlvo] = useState(settings.diasCoberturaAlvo);
  const [savedCalculationPeriodDays, setSavedCalculationPeriodDays] = useState<number | null>(settings.calculationPeriodDays);
  const [savedExcludeNoSalesNoStock, setSavedExcludeNoSalesNoStock] = useState(settings.excludeNoSalesNoStock);
  const [savedExcludeNoSalesWithStock, setSavedExcludeNoSalesWithStock] = useState(settings.excludeNoSalesWithStock);
  const [savedImportCnpjFilterEnabled, setSavedImportCnpjFilterEnabled] = useState(settings.importCnpjFilterEnabled);
  const [savedImportAllowedCnpjs, setSavedImportAllowedCnpjs] = useState<string[]>(settings.importAllowedCnpjs);
  const [savedOperationalScoreWeights, setSavedOperationalScoreWeights] = useState({...settings.operationalScoreWeights});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setLeadTime(settings.leadTime);
    setDiasCoberturaAlvo(settings.diasCoberturaAlvo);
    setCalculationPeriodDays(settings.calculationPeriodDays);
    setExcludeNoSalesNoStock(settings.excludeNoSalesNoStock);
    setExcludeNoSalesWithStock(settings.excludeNoSalesWithStock);
    setImportCnpjFilterEnabled(settings.importCnpjFilterEnabled);
    setImportAllowedCnpjs(settings.importAllowedCnpjs);
    setOperationalScoreWeights({...settings.operationalScoreWeights});
    setSavedExcludeNoSalesNoStock(settings.excludeNoSalesNoStock);
    setSavedExcludeNoSalesWithStock(settings.excludeNoSalesWithStock);
    setSavedLeadTime(settings.leadTime);
    setSavedDiasCoberturaAlvo(settings.diasCoberturaAlvo);
    setSavedCalculationPeriodDays(settings.calculationPeriodDays);
    setSavedImportCnpjFilterEnabled(settings.importCnpjFilterEnabled);
    setSavedImportAllowedCnpjs(settings.importAllowedCnpjs);
    setSavedOperationalScoreWeights({...settings.operationalScoreWeights});
  }, [settings.leadTime, settings.diasCoberturaAlvo, settings.calculationPeriodDays, settings.excludeNoSalesNoStock, settings.excludeNoSalesWithStock, settings.importCnpjFilterEnabled, settings.importAllowedCnpjs, settings.operationalScoreWeights]);

  const handleAddCnpj = () => {
    const normalized = normalizeCnpj(cnpjInput);
    if (normalized.length !== 14) {
      setCnpjInputError('Informe um CNPJ com 14 dígitos.');
      return;
    }
    if (importAllowedCnpjs.includes(normalized)) {
      setCnpjInputError('Este CNPJ já está na lista de permitidos.');
      return;
    }
    setImportAllowedCnpjs((current) => normalizeCnpjList([...current, normalized]));
    setCnpjInput('');
    setCnpjInputError('');
  };

  const handleRemoveCnpj = (cnpj: string) => {
    setImportAllowedCnpjs((current) => current.filter((item) => item !== cnpj));
    setCnpjInputError('');
  };

  const handleScoreWeightChange = (key: OperationalScoreWeightKey, percentage: number) => {
    const normalizedPercentage = Number.isFinite(percentage)
      ? Math.min(100, Math.max(0, percentage))
      : 0;
    setOperationalScoreWeights((current) => ({
      ...current,
      [key]: normalizedPercentage / 100,
    }));
  };

  const handleSave = async () => {
    if (settings.canManageImportCnpjs && importCnpjFilterEnabled && !importAllowedCnpjs.length) {
      setErrorMessage('Adicione pelo menos um CNPJ permitido antes de ativar o filtro de importação.');
      setSaveStatus('error');
      return;
    }
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage('');

    try {
      const response = await api.post<{ success: boolean; error?: string }>('/sellers/stock-intelligence/settings', {
        lead_time: leadTime,
        cobertura_alvo: diasCoberturaAlvo,
        calculation_period_days: calculationPeriodDays,
        exclude_no_sales_no_stock: excludeNoSalesNoStock,
        exclude_no_sales_with_stock: excludeNoSalesWithStock,
        operational_score_weights: operationalScoreWeights,
        ...(settings.canManageImportCnpjs ? {
          import_cnpj_filter_enabled: importCnpjFilterEnabled,
          import_allowed_cnpjs: normalizeCnpjList(importAllowedCnpjs),
        } : {}),
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao salvar configurações');
      }

      await reload();
      setSavedLeadTime(leadTime);
      setSavedDiasCoberturaAlvo(diasCoberturaAlvo);
      setSavedCalculationPeriodDays(calculationPeriodDays);
      setSavedImportCnpjFilterEnabled(importCnpjFilterEnabled);
      setSavedImportAllowedCnpjs(normalizeCnpjList(importAllowedCnpjs));
      setSavedOperationalScoreWeights({...operationalScoreWeights});
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
      setSavedExcludeNoSalesNoStock(excludeNoSalesNoStock);
      setSavedExcludeNoSalesWithStock(excludeNoSalesWithStock);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro ao salvar configurações no servidor';
      setErrorMessage(message);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setLeadTime(7);
    setDiasCoberturaAlvo(21);
    setCalculationPeriodDays(null);
    setExcludeNoSalesNoStock(false);
    setExcludeNoSalesWithStock(false);
    setImportCnpjFilterEnabled(false);
    setImportAllowedCnpjs(settings.knownImportCnpjs);
    setOperationalScoreWeights({...DEFAULT_OPERATIONAL_SCORE_WEIGHTS});
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage('');

    try {
      const response = await api.post<{ success: boolean; error?: string }>('/sellers/stock-intelligence/settings', {
        lead_time: 7,
        cobertura_alvo: 21,
        calculation_period_days: null,
        exclude_no_sales_no_stock: false,
        exclude_no_sales_with_stock: false,
        operational_score_weights: DEFAULT_OPERATIONAL_SCORE_WEIGHTS,
        ...(settings.canManageImportCnpjs ? {
          import_cnpj_filter_enabled: false,
          import_allowed_cnpjs: null,
        } : {}),
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao restaurar configurações');
      }
      await reload();

      setSavedLeadTime(7);
      setSavedDiasCoberturaAlvo(21);
      setSavedCalculationPeriodDays(null);
      setSavedExcludeNoSalesNoStock(false);
      setSavedExcludeNoSalesWithStock(false);
      setSavedImportCnpjFilterEnabled(false);
      setSavedImportAllowedCnpjs(settings.knownImportCnpjs);
      setSavedOperationalScoreWeights({...DEFAULT_OPERATIONAL_SCORE_WEIGHTS});
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erro ao restaurar configurações no servidor';
      setErrorMessage(message);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    leadTime !== savedLeadTime || diasCoberturaAlvo !== savedDiasCoberturaAlvo ||
    calculationPeriodDays !== savedCalculationPeriodDays ||
    excludeNoSalesNoStock !== savedExcludeNoSalesNoStock ||
    excludeNoSalesWithStock !== savedExcludeNoSalesWithStock ||
    !operationalScoreWeightsEqual(operationalScoreWeights, savedOperationalScoreWeights) ||
    (settings.canManageImportCnpjs && (
      importCnpjFilterEnabled !== savedImportCnpjFilterEnabled || !areCnpjListsEqual(importAllowedCnpjs, savedImportAllowedCnpjs)
    ));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className="w-full rounded-2xl border border-red-200 bg-white p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-950">N&atilde;o foi poss&iacute;vel confirmar as configura&ccedil;&otilde;es</h1>
            <p className="mt-2 text-sm text-gray-600">{settingsError}</p>
            <p className="mt-1 text-sm text-gray-500">Os valores padr&atilde;o n&atilde;o ser&atilde;o exibidos para evitar diverg&ecirc;ncia com os c&aacute;lculos do servidor.</p>
            <button type="button" onClick={() => reload()} className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-10">
      <div>
        <h1 className="mb-2 text-3xl font-bold tracking-tight text-gray-950">Configurações</h1>
        <p className="text-sm text-gray-500">
          Configure a cobertura alvo e os valores de segurança do sistema de reposição
        </p>
      </div>

      <div className="relative w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm lg:p-8">
        <CardHelp content="Estes parâmetros alteram os cálculos de estoque alvo, cobertura, status e pedido sugerido em todas as telas de Lojistas." className="absolute right-3 top-3" />
        <div className="mb-6 pr-8">
          <h2 className="text-xl font-bold text-gray-950">Parâmetros de cálculo</h2>
          <p className="mt-1 text-sm text-gray-500">Defina as referências usadas pelo modelo para calcular segurança e reposição.</p>
        </div>

        <div className="space-y-8">
          <div className="grid gap-5 lg:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
            <label htmlFor="leadTime" className="block text-sm font-medium text-gray-700 mb-2">
              Lead Time (dias)
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Valor de segurança usado somente quando o produto ainda não possui um fornecedor com lead time cadastrado.
            </p>
            <input
              type="number"
              id="leadTime"
              min="1"
              value={leadTime}
              onChange={(e) => setLeadTime(Number(e.target.value))}
              disabled={isSaving}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
            <label htmlFor="cobertura" className="block text-sm font-medium text-gray-700 mb-2">
              Dias de Cobertura Alvo
            </label>
            <p className="text-sm text-gray-500 mb-3">
              Meta de dias que o estoque deve cobrir após a reposição. Usado para calcular o estoque alvo ideal.
            </p>
            <input
              type="number"
              id="cobertura"
              min="1"
              value={diasCoberturaAlvo}
              onChange={(e) => setDiasCoberturaAlvo(Number(e.target.value))}
              disabled={isSaving}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <label htmlFor="calculationPeriodDays" className="block text-sm font-medium text-gray-700">
                  Dias considerados no c&aacute;lculo
                </label>
                <p className="mt-2 text-sm text-gray-500">
                  Define o divisor usado no CMD e, por consequ&ecirc;ncia, no estoque alvo e no pedido sugerido.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Usar per&iacute;odo manual"
                aria-checked={calculationPeriodDays !== null}
                disabled={isSaving}
                onClick={() => setCalculationPeriodDays((value) => value === null ? 30 : null)}
                className={`relative mt-0.5 h-6 w-11 flex-none rounded-full transition ${
                  calculationPeriodDays !== null ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${
                  calculationPeriodDays !== null ? 'left-6' : 'left-1'
                }`} />
              </button>
            </div>

            {calculationPeriodDays === null ? (
              <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-sm text-blue-900">
                Autom&aacute;tico: usa os dias reais do per&iacute;odo importado (28, 29, 30 ou 31).
              </div>
            ) : (
              <div className="mt-4">
                <input
                  type="number"
                  id="calculationPeriodDays"
                  min="1"
                  max="366"
                  value={calculationPeriodDays}
                  onChange={(event) => setCalculationPeriodDays(Number(event.target.value))}
                  disabled={isSaving}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Manual: o mesmo valor ser&aacute; aplicado a todos os produtos. Use 30 quando quiser padronizar o m&ecirc;s comercial em 30 dias.
                </p>
              </div>
            )}
          </div>

          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 lg:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Elegibilidade dos produtos</h3>
            <p className="text-sm text-gray-500 mb-4">Escolha quais produtos sem movimenta&ccedil;&atilde;o devem ficar fora de dashboards, reposi&ccedil;&atilde;o e pedidos.</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-xl border border-gray-200 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">Sem venda e sem estoque</p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">N&atilde;o calcular produtos com sa&iacute;da igual a zero e estoque atual igual a zero. Esses itens podem ter sa&iacute;do de linha ou deixado de fazer parte do mix.</p>
                </div>
                <CardHelp content="Quando ativado, produtos sem vendas e sem estoque deixam de participar dos endpoints e cálculos." className="mt-0.5 flex-none" />
                <button type="button" role="switch" aria-checked={excludeNoSalesNoStock} disabled={isSaving} onClick={() => setExcludeNoSalesNoStock((value) => !value)} className={`relative mt-1 h-6 w-11 flex-none rounded-full transition ${excludeNoSalesNoStock ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${excludeNoSalesNoStock ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-gray-200 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">Sem vendas, mas com estoque</p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">N&atilde;o calcular produtos com sa&iacute;da igual a zero que ainda possuam estoque na loja.</p>
                </div>
                <CardHelp content="Quando ativado, produtos sem vendas, mesmo com estoque, deixam de participar dos endpoints e cálculos." className="mt-0.5 flex-none" />
                <button type="button" role="switch" aria-checked={excludeNoSalesWithStock} disabled={isSaving} onClick={() => setExcludeNoSalesWithStock((value) => !value)} className={`relative mt-1 h-6 w-11 flex-none rounded-full transition ${excludeNoSalesWithStock ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${excludeNoSalesWithStock ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 lg:p-6">
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900">Pesos do índice operacional</h3>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Defina quanto cada condição reduz a nota de 100 pontos. Um peso de 0% não penaliza; 100% aplica a penalização máxima por combinação de produto e CNPJ.
                </p>
              </div>
              <CardHelp
                content="Fórmula: 100 menos a soma de (quantidade de produtos em cada condição × peso), dividida pelo total de produtos e multiplicada por 100."
                className="mt-0.5 flex-none"
              />
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {OPERATIONAL_SCORE_WEIGHT_OPTIONS.map((option) => {
                const inputId = `operational-score-weight-${option.key}`;
                const descriptionId = `${inputId}-description`;
                return (
                  <div key={option.key} className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                    <label htmlFor={inputId} className="block text-sm font-semibold text-gray-900">
                      {option.label}
                    </label>
                    <p id={descriptionId} className="mt-1 min-h-[2.5rem] text-xs leading-5 text-gray-500">
                      {option.description}
                    </p>
                    <div className="relative mt-3">
                      <input
                        id={inputId}
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        inputMode="decimal"
                        value={Number((operationalScoreWeights[option.key] * 100).toFixed(2))}
                        onChange={(event) => handleScoreWeightChange(option.key, Number(event.target.value))}
                        disabled={isSaving}
                        aria-describedby={descriptionId}
                        className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 tabular-nums outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-medium text-gray-500">%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs leading-5 text-blue-900">
              A nota considera a proporção de combinações de produto e CNPJ em cada condição no filtro atual. Combinações saudáveis não retiram pontos.
            </p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 lg:p-6">
            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900">Controle dos CNPJs importados</h3>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Limite novos imports aos CNPJs autorizados para este lojista.
                </p>
              </div>
              <CardHelp content="A lista funciona somente como autorização. Adicionar um CNPJ aqui não cria loja nem inclui esse CNPJ nos dashboards; ele passa a aparecer nos dados somente depois de um import válido." className="mt-0.5 flex-none" />
              <button
                type="button"
                role="switch"
                aria-label="Importar somente de CNPJs específicos"
                aria-checked={importCnpjFilterEnabled}
                disabled={isSaving || !settings.canManageImportCnpjs}
                onClick={() => {
                  setImportCnpjFilterEnabled((value) => !value);
                  setCnpjInputError('');
                }}
                className={`relative mt-1 h-6 w-11 flex-none rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${importCnpjFilterEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${importCnpjFilterEnabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/70 p-4">
              <p className="font-medium text-gray-900">Importar somente de CNPJs específicos</p>
              {!settings.canManageImportCnpjs ? (
                <p className="mt-2 text-sm text-amber-700">
                  Seu usuário possui acesso restrito por CNPJ. Somente um administrador com acesso a todos os CNPJs pode alterar esta regra.
                </p>
              ) : !importCnpjFilterEnabled ? (
                <p className="mt-2 text-sm text-gray-500">
                  Desativado: o import continua aceitando qualquer CNPJ permitido para o usuário.
                </p>
              ) : (
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={cnpjInput}
                        onChange={(event) => {
                          setCnpjInput(event.target.value);
                          setCnpjInputError('');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleAddCnpj();
                          }
                        }}
                        disabled={isSaving}
                        placeholder="00.000.000/0000-00"
                        aria-label="Novo CNPJ permitido"
                        className="min-w-0 flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <button
                        type="button"
                        onClick={handleAddCnpj}
                        disabled={isSaving || !cnpjInput.trim()}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                        Adicionar
                      </button>
                    </div>
                    {cnpjInputError && <p className="mt-2 text-sm text-red-600">{cnpjInputError}</p>}
                    <p className="mt-2 text-xs leading-5 text-gray-500">
                      CNPJs adicionados manualmente ficam apenas nesta autorização e não aparecem nas demais telas até receberem um import.
                    </p>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-700">CNPJs permitidos</p>
                      <span className="text-xs text-gray-500">{importAllowedCnpjs.length} na lista</span>
                    </div>
                    {importAllowedCnpjs.length ? (
                      <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                        {importAllowedCnpjs.map((cnpj) => {
                          const alreadyImported = settings.knownImportCnpjs.includes(cnpj);
                          return (
                            <div key={cnpj} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                              <span className="min-w-0 flex-1 font-mono text-sm text-gray-900">{formatCnpj(cnpj)}</span>
                              <span className={`rounded-full px-2 py-1 text-xs font-medium ${alreadyImported ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                                {alreadyImported ? 'Já importado' : 'Pré-autorizado'}
                              </span>
                              <button type="button" onClick={() => handleRemoveCnpj(cnpj)} disabled={isSaving} title="Remover da lista de permitidos" className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                        Adicione pelo menos um CNPJ. Enquanto a lista estiver vazia, esta configuração não poderá ser salva ativa.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 border-t border-gray-200 pt-6">
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition ${
              hasChanges && !isSaving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </button>

          <button
            onClick={handleReset}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-5 h-5" />
            Restaurar Padrões
          </button>
        </div>

        {saveStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-700 flex-shrink-0" />
            <p className="text-sm text-green-900 font-medium">
              Configurações salvas com sucesso! Os cálculos serão atualizados automaticamente.
            </p>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
            <p className="text-sm text-red-900 font-medium">{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="w-full rounded-2xl border border-blue-200 bg-blue-50 p-5">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Como isso afeta os cálculos?</h3>
        <ul className="grid gap-3 text-sm text-blue-900 md:grid-cols-2 xl:grid-cols-4">
          <li className="rounded-xl border border-blue-100 bg-white/60 p-3 leading-5">O Lead Time global é um fallback; produtos vinculados usam o valor do fornecedor</li>
          <li className="rounded-xl border border-blue-100 bg-white/60 p-3 leading-5">A Cobertura Alvo define quanto estoque você deseja manter após a reposição</li>
          <li className="rounded-xl border border-blue-100 bg-white/60 p-3 leading-5">Valores mais altos aumentam a segurança mas elevam o custo de estoque</li>
          <li className="rounded-xl border border-blue-100 bg-white/60 p-3 leading-5">Todas as mudanças são aplicadas instantaneamente na tabela e no dashboard</li>
        </ul>
      </div>
    </div>
  );
};
